import React from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Button,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { notificationsAPI } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import { getNotificationCategoryMeta } from '../../utils/notificationCategoryMeta';

const CATEGORY_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'accommodation', label: 'Accommodation' },
  { value: 'customer_registration', label: 'Customer registration' },
  { value: 'documentation', label: 'Documentation' },
  { value: 'promotion', label: 'Promotion' },
  { value: 'event', label: 'Events' },
  { value: 'contact', label: 'Contact' },
  { value: 'user', label: 'User' },
  { value: 'support', label: 'Customer Support' },
];

const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString();
};

const NotificationsPage = () => {
  const { locale, t } = useLanguage();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = React.useState(false);
  const [markAllLoading, setMarkAllLoading] = React.useState(false);
  const [notifications, setNotifications] = React.useState([]);
  const [filter, setFilter] = React.useState('all');
  const [page, setPage] = React.useState(1);
  const [pageSize] = React.useState(20);
  const [total, setTotal] = React.useState(0);

  const fetchNotifications = React.useCallback(async (nextPage = 1) => {
    setLoading(true);
    try {
      const res = await notificationsAPI.getNotifications(nextPage, pageSize, locale);
      const payload = res?.data?.payload || {};
      const list = payload?.notifications?.items || [];
      setNotifications(Array.isArray(list) ? list : []);
      setTotal(Number(payload?.notifications?.total || 0));
      setPage(Number(payload?.notifications?.page || nextPage));
    } finally {
      setLoading(false);
    }
  }, [pageSize, locale]);

  React.useEffect(() => {
    fetchNotifications(1);
  }, [fetchNotifications]);

  const handleReadOne = async (item) => {
    if (!item || item.isRead) return;
    setNotifications((prev) => prev.map((n) => (n.id === item.id ? { ...n, isRead: true } : n)));
    try {
      await notificationsAPI.markAsRead(item.id);
    } catch {
      fetchNotifications(page);
    }
  };

  const handleReadAll = async () => {
    setMarkAllLoading(true);
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications((prev) => prev.map((item) => ({ ...item, isRead: true })));
    } finally {
      setMarkAllLoading(false);
    }
  };

  const filteredNotifications = React.useMemo(() => {
    if (filter === 'all') return notifications;
    return notifications.filter((item) => getNotificationCategoryMeta(item).filter === filter);
  }, [filter, notifications]);

  const hasNextPage = page * pageSize < total;
  const hasPrevPage = page > 1;

  return (
    <Box>
      <Paper sx={{ p: { xs: 1.5, sm: 2.5 }, borderRadius: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1.25,
            mb: 2,
          }}
        >
          <Typography variant="h6" fontWeight={700}>
            {t('notifications.title')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.25, flexDirection: { xs: 'column', sm: 'row' } }}>
            <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220 } }}>
              <InputLabel id="notification-filter-label">{t('notifications.filterByCategory')}</InputLabel>
              <Select
                labelId="notification-filter-label"
                value={filter}
                label={t('notifications.filterByCategory')}
                onChange={(e) => setFilter(e.target.value)}
              >
                {CATEGORY_FILTERS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {t(`notifications.category.${item.value}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={handleReadAll} disabled={markAllLoading} fullWidth={isMobileView}>
              {markAllLoading ? t('common.pleaseWait') : t('notifications.markAllRead')}
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={26} />
          </Box>
        ) : filteredNotifications.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('notifications.noNotifications')}
          </Typography>
        ) : (
          <Box sx={{ display: 'grid', gap: 1.25 }}>
            {filteredNotifications.map((item) => {
              const meta = getNotificationCategoryMeta(item);
              const CategoryIcon = meta.icon;
              return (
                <Paper
                  key={item.id}
                  variant="outlined"
                  onClick={() => handleReadOne(item)}
                  sx={{
                    p: { xs: 1.25, sm: 1.5 },
                    borderRadius: 2,
                    cursor: 'pointer',
                    borderColor: item.isRead ? 'divider' : 'primary.light',
                    bgcolor: item.isRead ? 'background.paper' : 'rgba(25, 118, 210, 0.04)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: meta.color,
                        color: 'white',
                        flexShrink: 0,
                      }}
                    >
                      <CategoryIcon fontSize="small" />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start' }}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {item.title}
                        </Typography>
                        <Chip
                          label={item.isRead ? t('notifications.status.read') : t('notifications.status.unread')}
                          color={item.isRead ? 'default' : 'primary'}
                          size="small"
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {item.body}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                        {formatDateTime(item.createdAtUtc)}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1, flexDirection: 'row' }}>
          <Button
            variant="outlined"
            disabled={!hasPrevPage || loading}
            onClick={() => fetchNotifications(page - 1)}
            fullWidth={isMobileView}
          >
            {t('common.previous')}
          </Button>
          <Button
            variant="outlined"
            disabled={!hasNextPage || loading}
            onClick={() => fetchNotifications(page + 1)}
            fullWidth={isMobileView}
          >
            {t('common.next')}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default NotificationsPage;
